export type RecentSearch = {
	query: string;
	ts: number;
};

export type RecentViewType =
	| 'route'
	| 'dashboard_tab'
	| 'invoices_tab'
	| 'inventory_tab'
	| 'reporting_tab'
	| 'order'
	| 'invoice'
	| 'rekey'
	| 'ems';

export type RecentView = {
	type: RecentViewType;
	/**
	 * Stable unique key for de-duping. Examples:
	 * - route:/invoices
	 * - dashboard_tab:all
	 * - ems:abc123
	 */
	key: string;
	label: string;
	ts: number;
	path?: string;
	emsId?: string;
	orderId?: string;
	invoiceId?: string;
	rekeyId?: string;
	meta?: Record<string, unknown>;
};

export type RecentActivityData = {
	version: 1;
	searches: RecentSearch[];
	views: RecentView[];
};

const VERSION: RecentActivityData['version'] = 1;
const MAX_SEARCHES = 10;
const MAX_VIEWS = 15;

export function getRecentActivityStorageKey(userId: string | null | undefined) {
	return `partbay.recentActivity.v${VERSION}:${userId ?? 'anon'}`;
}

function safeParse(json: string | null): any {
	if (!json) return null;
	try {
		return JSON.parse(json);
	} catch {
		return null;
	}
}

export function readRecentActivity(userId: string | null | undefined): RecentActivityData {
	if (typeof window === 'undefined') {
		return { version: VERSION, searches: [], views: [] };
	}
	const key = getRecentActivityStorageKey(userId);
	const parsed = safeParse(window.localStorage.getItem(key));
	if (!parsed || parsed.version !== VERSION) {
		return { version: VERSION, searches: [], views: [] };
	}
	return {
		version: VERSION,
		searches: Array.isArray(parsed.searches) ? parsed.searches : [],
		views: Array.isArray(parsed.views) ? parsed.views : [],
	};
}

export function writeRecentActivity(userId: string | null | undefined, data: RecentActivityData) {
	if (typeof window === 'undefined') return;
	const key = getRecentActivityStorageKey(userId);
	try {
		window.localStorage.setItem(key, JSON.stringify(data));
		// Same-tab sync for components using the hook.
		window.dispatchEvent(new Event('partbay:recentActivityUpdated'));
	} catch {
		// ignore storage errors (quota, private mode, etc.)
	}
}

function dedupeAndPrepend<T>(
	list: T[],
	item: T,
	isSame: (a: T, b: T) => boolean,
	max: number
): T[] {
	const next = [item, ...list.filter((el) => !isSame(el, item))];
	return next.slice(0, max);
}

export function addRecentSearch(userId: string | null | undefined, query: string): RecentActivityData {
	const trimmed = query.trim();
	if (trimmed.length < 2) return readRecentActivity(userId);
	const now = Date.now();
	const existing = readRecentActivity(userId);
	const next: RecentActivityData = {
		...existing,
		searches: dedupeAndPrepend(
			existing.searches,
			{ query: trimmed, ts: now },
			(a, b) => a.query.toLowerCase() === b.query.toLowerCase(),
			MAX_SEARCHES
		),
	};
	writeRecentActivity(userId, next);
	return next;
}

export function addRecentView(userId: string | null | undefined, view: Omit<RecentView, 'ts'>): RecentActivityData {
	const now = Date.now();
	const existing = readRecentActivity(userId);
	const next: RecentActivityData = {
		...existing,
		views: dedupeAndPrepend(
			existing.views,
			{ ...view, ts: now },
			(a, b) => a.key === b.key,
			MAX_VIEWS
		),
	};
	writeRecentActivity(userId, next);
	return next;
}

export function clearRecentSearches(userId: string | null | undefined): RecentActivityData {
	const existing = readRecentActivity(userId);
	const next: RecentActivityData = { ...existing, searches: [] };
	writeRecentActivity(userId, next);
	return next;
}

export function clearRecentViews(userId: string | null | undefined): RecentActivityData {
	const existing = readRecentActivity(userId);
	const next: RecentActivityData = { ...existing, views: [] };
	writeRecentActivity(userId, next);
	return next;
}


